// Fixed-schema CSV exports (brief §9). The raw CSV records the chosen option per
// item (not just right/wrong) so downstream item/distractor analysis is possible,
// and includes a trailing ANSWER_KEY row. Schemas must stay stable for merging.
import type { QuizRecord, ResultRecord, StudentRecord, AnswerKeyRecord } from '../db/schema.js';
import { difficultyBand, discriminationBand, type QuizAnalysis } from '../analysis/itemAnalysis.js';
import type { CompetencyGapReport } from '../analysis/competencyAnalysis.js';
import { formatItemSpec } from '../analysis/competencyMap.js';
import { correctOptionFor } from '../scoring/score.js';

function csvCell(v: unknown): string {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}
const csvRow = (cells: unknown[]) => cells.map(csvCell).join(',');

/** Per-item cell: chosen option, or '-' blank, '*' double-mark. */
function answerCell(r: ResultRecord, q: number): string {
  const a = r.rawAnswers.find((x) => x.q === q);
  if (!a) return '';
  if (a.flags.includes('double')) return '*';
  if (a.flags.includes('blank') || a.chosen === null) return '-';
  return a.chosen;
}

/** One student's row context (section/date vary across assignments in a rollup). */
export interface CsvRow {
  result: ResultRecord;
  gradeLevel: string;
  section: string;
  date: string;
}

export function buildRawCsv(
  quiz: QuizRecord,
  rows: CsvRow[],
  students: StudentRecord[],
  key: AnswerKeyRecord | null,
  meta: { school?: string; teacher?: string } = {},
): string {
  const nameOf = new Map(students.map((s) => [s.id, s]));
  const qs = Array.from({ length: quiz.items }, (_, i) => i + 1);
  const header = ['school', 'grade', 'section', 'subject', 'quiz_id', 'quiz_title', 'date', 'teacher', 'lrn', 'name', 'score', 'items', ...qs.map((q) => 'Q' + q)];

  const lines = [csvRow(header)];
  for (const { result: r, gradeLevel, section, date } of rows) {
    const stu = r.studentId ? nameOf.get(r.studentId) : undefined;
    // LRN: matched student's, else the 12-digit LRN read from the sheet (an
    // unregistered examinee whose LRN is still known), else blank for a
    // captured-name result (§7A), else unassigned.
    const lrn = stu?.lrn ?? (/^\d{12}$/.test(r.idDigitsRead) ? r.idDigitsRead : r.capturedName ? '' : 'UNASSIGNED');
    const name = stu?.name ?? r.capturedName ?? '';
    lines.push(
      csvRow([meta.school ?? '', gradeLevel, section, quiz.subject, quiz.id, quiz.title, date, meta.teacher ?? '', lrn, name, r.score ?? '', quiz.items, ...qs.map((q) => answerCell(r, q))]),
    );
  }
  if (key) {
    lines.push(csvRow([meta.school ?? '', quiz.gradeLevel, '', quiz.subject, quiz.id, quiz.title, '', meta.teacher ?? '', 'ANSWER_KEY', '', '', quiz.items, ...qs.map((q) => correctOptionFor(key, q) ?? '')]));
  }
  return lines.join('\n');
}

const dec = (v: number | null) => (v == null ? '' : v.toFixed(4));

/** Item analysis CSV — question_stats (§1.4) columns plus the §1.6 report
 *  fields (upper/lower group counts and derived Level + Decision per metric),
 *  then the distractor columns. The distractor block is APPENDED at the end and
 *  nothing before it moves: the schema has to stay stable so a downstream merge
 *  of an older export still lines up. */
export function buildItemAnalysisCsv(quiz: QuizRecord, analysis: QuizAnalysis): string {
  const opts = analysis.options;
  const header = [
    'item', 'key', 'total_responses', 'correct_count',
    'upper_group_correct', 'n_upper', 'lower_group_correct', 'n_lower',
    'difficulty_idx', 'difficulty_level', 'difficulty_decision',
    'disc_index', 'disc_level', 'disc_decision',
    'point_biserial', 'blank', 'double', ...opts.map((o) => 'chose_' + o), 'flag',
    // appended (distractor analysis)
    ...opts.map((o) => 'pct_' + o),
    ...opts.flatMap((o) => ['upper_chose_' + o, 'lower_chose_' + o]),
    'non_functioning_distractors', 'attractive_distractors',
  ];
  const lines = [csvRow(header)];
  for (const it of analysis.items) {
    const db = difficultyBand(it.difficulty);
    const disc = discriminationBand(it.discrimination);
    lines.push(
      csvRow([
        it.q,
        it.key,
        it.total,
        it.correct,
        it.upperCorrect ?? '',
        it.nUpper,
        it.lowerCorrect ?? '',
        it.nLower,
        it.difficulty.toFixed(4),
        db.level,
        db.decision,
        dec(it.discrimination),
        disc.level,
        disc.decision ?? '',
        dec(it.pointBiserial),
        it.blank,
        it.double,
        ...opts.map((o) => it.distractors[o] ?? 0),
        it.flag ?? '',
        // Derived distractor fields are blank (not 0) when the guardrail suppresses
        // them — a fabricated 0 would read as "no learner chose this".
        ...opts.map((o) => it.optionPct?.[o] ?? ''),
        ...opts.flatMap((o) => [it.upperByOption?.[o] ?? '', it.lowerByOption?.[o] ?? '']),
        it.nonFunctioning == null ? '' : it.nonFunctioning.join(' '),
        it.attractive == null ? '' : it.attractive.join(' '),
      ]),
    );
  }
  lines.push('');
  lines.push(csvRow(['students', analysis.n]));
  lines.push(csvRow(['mean_score', analysis.meanScore, 'of', analysis.maxScore]));
  // SD as a share of max and the band counts live in the MPS CSV — that is the
  // quiz-level export; this one is per item.
  lines.push(csvRow(['std_dev', analysis.stdDev]));
  lines.push(csvRow(['mps_percent', analysis.mps]));
  lines.push(csvRow(['mastery_level', analysis.masteryLevel]));
  lines.push(csvRow(['kr20', dec(analysis.kr20)]));
  lines.push(csvRow(['highest', analysis.highest]));
  lines.push(csvRow(['lowest', analysis.lowest]));
  return lines.join('\n');
}

/**
 * MPS report CSV (§2.6): a per-item percentage-correct table with a Below-75%
 * flag, then the overall subject-level summary (school_subject_stats, §2.4).
 */
export function buildMpsCsv(quiz: QuizRecord, analysis: QuizAnalysis, section: string): string {
  const lines = [csvRow(['item', 'correct_count', 'total_responses', 'item_mps', 'below_75'])];
  for (const it of analysis.items) {
    const itemMps = Math.round(it.difficulty * 100);
    lines.push(csvRow([it.q, it.correct, it.total, itemMps, itemMps < 75 ? 'Yes' : '']));
  }
  lines.push('');
  // Appended only — the columns above keep their positions so downstream merges
  // of an older export still line up (§12.4).
  const header = ['quiz_id', 'quiz_title', 'subject', 'grade', 'section', 'total_items', 'student_count', 'total_score_sum', 'mean_score', 'std_dev', 'mps', 'mastery_level', 'kr20', 'highest', 'lowest', 'score_distribution',
    'sd_pct_of_max', ...analysis.bandDistribution.map((b) => 'band_' + b.range.replace('–', '_')),
  ];
  const row = [
    quiz.id,
    quiz.title,
    quiz.subject,
    quiz.gradeLevel,
    section,
    quiz.items,
    analysis.n,
    analysis.totalScoreSum,
    analysis.meanScore,
    analysis.stdDev,
    analysis.mps,
    analysis.masteryLevel,
    dec(analysis.kr20),
    analysis.highest,
    analysis.lowest,
    JSON.stringify(analysis.distribution.map((d) => [d.score, d.count])),
    analysis.sdPctOfMax,
    ...analysis.bandDistribution.map((b) => b.count),
  ];
  lines.push(csvRow(header));
  lines.push(csvRow(row));
  return lines.join('\n');
}

/**
 * Competency analysis CSV (report #1): a per-competency summary, the per-item
 * breakdown inside each competency, then a long-form remediation roster — one row
 * per (competency, learner below the 75 passing mark) carrying that learner's DepEd
 * instructional response, so it can be pasted straight into a remediation plan.
 */
export function buildCompetencyAnalysisCsv(quiz: QuizRecord, report: CompetencyGapReport, section: string): string {
  const lines = [
    csvRow([
      'competency', 'learning_competency', 'cognitive_level', 'items', 'n_items',
      'points_per_learner', 'students', 'class_pct', 'mastery_level',
      'class_instructional_response', 'class_mastered', 'mastered_learners',
      'needs_remediation', 'targeted_remediation', 'sustained_intervention',
      'guided_practice', 'enough_items',
    ]),
  ];
  for (const c of report.competencies) {
    lines.push(
      csvRow([
        c.label,
        c.description,
        c.cognitiveLevel ?? '',
        formatItemSpec(c.items),
        c.items.length,
        c.possiblePerLearner,
        c.n,
        c.classPct ?? '',
        c.band,
        // Class average, not a per-learner verdict — the roster below carries those.
        c.classPct == null ? '' : c.intervention.classResponse,
        c.classPct == null ? '' : c.mastered ? 'Yes' : 'No',
        c.masteredCount,
        c.needing.length,
        c.targeted.length,
        c.sustained.length,
        c.guidedPractice.length,
        c.reliable ? 'Yes' : 'No',
      ]),
    );
  }
  lines.push('');
  lines.push(csvRow(['PER-ITEM BREAKDOWN']));
  lines.push(csvRow(['competency', 'learning_competency', 'item', 'correct_count', 'total_responses', 'item_mps', 'below_75']));
  for (const c of report.competencies) {
    for (const it of c.itemStats) {
      lines.push(csvRow([c.label, c.description, it.q, it.correct, it.total, it.pct, it.pct < report.cutoff ? 'Yes' : '']));
    }
  }
  lines.push('');
  lines.push(csvRow(['LEARNERS NEEDING REMEDIATION']));
  lines.push(
    csvRow([
      'competency', 'learning_competency', 'lrn', 'name', 'earned', 'possible',
      'pct', 'deped_descriptor', 'instructional_response', 'section',
    ]),
  );
  for (const c of report.competencies) {
    for (const l of c.needing) {
      lines.push(
        csvRow([
          c.label, c.description, l.lrn, l.name, l.earned, l.possible, l.pct,
          l.intervention.descriptor, l.intervention.response, section,
        ]),
      );
    }
  }
  lines.push('');
  lines.push(csvRow(['LEARNERS FOR GUIDED PRACTICE (passing, 75-79)']));
  lines.push(csvRow(['competency', 'learning_competency', 'lrn', 'name', 'pct', 'deped_descriptor', 'section']));
  for (const c of report.competencies) {
    for (const l of c.guidedPractice) {
      lines.push(csvRow([c.label, c.description, l.lrn, l.name, l.pct, l.intervention.descriptor, section]));
    }
  }
  lines.push('');
  lines.push(csvRow(['quiz_id', quiz.id]));
  lines.push(csvRow(['quiz_title', quiz.title]));
  lines.push(csvRow(['subject', quiz.subject]));
  lines.push(csvRow(['grade', quiz.gradeLevel]));
  lines.push(csvRow(['section', section]));
  lines.push(csvRow(['students', report.n]));
  lines.push(csvRow(['passing_mark', report.cutoff]));
  lines.push(csvRow(['unmapped_items', formatItemSpec(report.unmappedItems)]));
  return lines.join('\n');
}

/**
 * Per-learner mastery CSV (report #2): a learner × competency percentage matrix,
 * with each learner's mastered and least-mastered competencies named outright.
 * Columns use the positional labels; the legend block maps them to the full
 * Learning Competency Descriptions.
 */
export function buildLearnerMasteryCsv(quiz: QuizRecord, report: CompetencyGapReport, section: string): string {
  const header = [
    'lrn', 'name', 'section', 'score', 'items', 'overall_pct',
    ...report.competencies.map((c) => 'pct_' + c.label),
    'mastered_count', 'least_mastered_count', 'mastered', 'least_mastered',
    'weakest_competency', 'weakest_pct', 'instructional_response',
  ];
  const lines = [csvRow(header)];
  for (const l of report.learners) {
    const weakest = l.leastMastered[0];
    lines.push(
      csvRow([
        l.lrn,
        l.name,
        section,
        l.score,
        l.maxScore,
        l.overallPct,
        ...l.scores.map((s) => s.pct ?? ''),
        l.mastered.length,
        l.leastMastered.length,
        l.mastered.map((s) => s.label).join('; '),
        l.leastMastered.map((s) => s.label).join('; '),
        weakest ? weakest.description : '',
        weakest ? weakest.pct : '',
        weakest ? weakest.intervention.response : '',
      ]),
    );
  }
  lines.push('');
  lines.push(csvRow(['COMPETENCY LEGEND']));
  lines.push(csvRow(['competency', 'learning_competency', 'cognitive_level', 'items']));
  for (const c of report.competencies) {
    lines.push(csvRow([c.label, c.description, c.cognitiveLevel ?? '', formatItemSpec(c.items)]));
  }
  lines.push('');
  lines.push(csvRow(['quiz_title', quiz.title]));
  lines.push(csvRow(['passing_mark', report.cutoff]));
  lines.push(csvRow(['min_items_for_learner_claim', report.minItemsForLearner]));
  return lines.join('\n');
}

/** Trigger a client-side file download (no server). */
export function downloadText(filename: string, text: string, mime = 'text/csv') {
  downloadBlob(filename, new Blob([text], { type: mime + ';charset=utf-8' }));
}

function downloadBlob(filename: string, blob: Blob) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(a.href);
    a.remove();
  }, 150);
}

export function safeName(s: string): string {
  return (s || 'export').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '').toLowerCase() || 'export';
}

/**
 * On phones, share the file via the Web Share API (goes to email/Drive/Messenger);
 * on desktop (or if sharing files isn't supported) fall back to a download.
 * Returns how it was delivered.
 */
export async function shareOrDownload(filename: string, text: string, mime = 'text/csv'): Promise<'shared' | 'downloaded'> {
  const nav = navigator as Navigator & { canShare?: (d: any) => boolean };
  try {
    if (typeof File !== 'undefined' && nav.canShare) {
      const file = new File([text], filename, { type: mime });
      if (nav.canShare({ files: [file] })) {
        await nav.share({ files: [file], title: filename });
        return 'shared';
      }
    }
  } catch {
    /* user cancelled or share failed — fall through to download */
  }
  downloadText(filename, text, mime);
  return 'downloaded';
}

/**
 * Same share-or-download path for binary files (the .xlsx workbook). Kept beside
 * the CSV helper so every export leaves the app the same way: share sheet on a
 * phone, plain download on desktop.
 */
export async function shareOrDownloadBytes(
  filename: string,
  bytes: Uint8Array,
  mime: string,
): Promise<'shared' | 'downloaded'> {
  const nav = navigator as Navigator & { canShare?: (d: any) => boolean };
  // Copy into a fresh ArrayBuffer: Blob rejects a SharedArrayBuffer-backed view,
  // and a subarray would otherwise carry its parent buffer's full length.
  const buf = bytes.slice().buffer as ArrayBuffer;
  try {
    if (typeof File !== 'undefined' && nav.canShare) {
      const file = new File([buf], filename, { type: mime });
      if (nav.canShare({ files: [file] })) {
        await nav.share({ files: [file], title: filename });
        return 'shared';
      }
    }
  } catch {
    /* user cancelled or share failed — fall through to download */
  }
  downloadBlob(filename, new Blob([buf], { type: mime }));
  return 'downloaded';
}
